import React, { useState, useMemo, useEffect, useCallback } from 'react';
import apiClient from '../api/client';
import { toast } from 'sonner';
import { normalizeSearchText } from '../utils/stringUtils';
import { canonicalizeReasonType } from '../utils/leaveUtils';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { DateRangePicker } from './ui/DateRangePicker';
import { 
  Search, Calendar, Clock, CheckCircle2, XCircle, Users, Download, 
  LayoutGrid, List, Columns, Filter, ChevronLeft, ChevronRight, AlertTriangle, MessageSquare, Trash2,
  UserCheck
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Label } from './ui/label';
import LeaveRequestCard from './LeaveRequestCard';
import LeaveRequestSummaryRow from './LeaveRequestSummaryRow';
import { useRoleFlags } from '@/auth/useRoleFlags';

const ITEMS_PER_PAGE = 20;
const PAGE_SIZE_OPTIONS = [20, 50, 100];
const LEAVE_VIEW_STORAGE_KEY = 'leave-management-view';
// Backend /leave-requests çağrısı limit=300 ile yapılıyor. Liste bu sayıya
// dayandığında daha fazlası olup olmadığını BİLEMEYİZ — kullanıcıya bunu
// gizlemek yerine bilgilendirici bir not gösteriyoruz (hata değil).
const LOADED_LIMIT = 300;

const getApproverRoleLabel = (role) => {
  const r = (role || '').trim();
  switch (r) {
    case 'formen':
      return 'FORMEN';
    case 'bolge_muhendisi':
      return 'BM';
    case 'mudur_yardimcisi':
      return 'MY';
    case 'mudur':
      return 'MÜDÜR';
    default:
      return r ? r.toUpperCase() : '';
  }
};

const getCurrentApproverPerson = (request, personnel) => {
  if (!Array.isArray(personnel) || personnel.length === 0) return null;
  if (request?.current_approver_id) {
    return personnel.find(p => p.id === request.current_approver_id) || null;
  }
  const chain = request?.approval_chain || [];
  if (!Array.isArray(chain) || chain.length === 0) return null;
  const history = request?.approval_history || [];
  const lastApproverId = history.length > 0 ? history[history.length - 1]?.approver_id : null;
  if (lastApproverId && chain.includes(lastApproverId)) {
    const idx = chain.indexOf(lastApproverId);
    const nextId = chain[idx + 1];
    if (nextId) {
      return personnel.find(p => p.id === nextId) || null;
    }
  }
  return personnel.find(p => p.id === chain[0]) || null;
};

const LeaveManagementPanel = ({
  leaveRequests,
  personnel,
  regions,
  parks = [],
  personnelId,
  userRole,
  filterApprovalHistory,
  onActionClick,
  onUpdate,
  requestViewMode = 'inbox',
  onRequestViewModeChange,
  leaveTypeTabs,
  setLeaveTypeTabs,
  lowLeavePersonnel = [],
  onOpenPersonnelDetails,
  onEditPersonnel,
  mudurSocialReportOverrideEnabled = true,
  onClearLeaveTestData,
  isReadOnly = false
}) => {
  // Sprint 2 F4: Dinamik yetki + rol flag'leri
  const _flagsCtx = useRoleFlags(userRole);
  const { has, isAdmin, isMudur, isMudurYardimcisi, isBolgeMuhendisi, isFormen, isParkGorevlisi, isGolgeAdmin } = _flagsCtx;

  // İzin talebi kartı/satırındaki personel adına tıklayınca personel kartını
  // "İzinler" sekmesinde aç — yıllık/sosyal/rapor durumuna hızlı bakış.
  // Hedef: PersonnelForm kartı (Genel/Özet/Görevler/İzinler/... sekmeleri) —
  // onEditPersonnel bunu 'leaves' sekmesiyle açar. Bu prop yoksa daha dar olan
  // PersonnelDetailsDialog'a düş; ikisi de yoksa ad tıklanabilir olmaz.
  const handleOpenPersonnelDetailFromRequest = useMemo(() => {
    // Salt-okunur görünümde (gölge admin / devreden yönetici) düzenleme formu
    // açılmaz — "Personel İzin Durumu" satır tıklamasıyla (isReadOnly guard'ı)
    // aynı kural.
    const openEdit = (!isReadOnly && typeof onEditPersonnel === 'function') ? onEditPersonnel : null;
    const openDetails = typeof onOpenPersonnelDetails === 'function' ? onOpenPersonnelDetails : null;
    if (!openEdit && !openDetails) return null;
    return (request) => {
      const targetId = request?.personnel_id;
      const person = (personnel || []).find((p) => String(p.id) === String(targetId));
      if (!person) {
        toast.warning('Personel kaydı listenizde bulunamadı; izin detayı açılamıyor.');
        return;
      }
      if (openEdit) openEdit(person);
      else openDetails(person, 'leaves');
    };
  }, [isReadOnly, onEditPersonnel, onOpenPersonnelDetails, personnel]);

  // Görünüm state'leri
  // 'card' | 'table' | 'decision'. Varsayılan 'card' — mevcut kullanıcı alışkanlığı
  // korunur; karar listesi ancak kullanıcı seçerse açılır. Tercih tarayıcıda saklanır.
  const [viewMode, setViewMode] = useState(() => {
    try {
      const stored = window.localStorage.getItem(LEAVE_VIEW_STORAGE_KEY);
      return ['card', 'table', 'decision'].includes(stored) ? stored : 'card';
    } catch (err) {
      return 'card';
    }
  });
  const changeViewMode = useCallback((next) => {
    setViewMode(next);
    try {
      window.localStorage.setItem(LEAVE_VIEW_STORAGE_KEY, next);
    } catch (err) {
      // Gizli sekmede yazılamayabilir — tercih yalnız bu oturumda geçerli olur.
    }
  }, []);
  // Karar listesinde seçili kayıt: TÜM nesne değil yalnız id tutulur, nesne her
  // render'da güncel listeden türetilir (bayat kopya riski yok).
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [pageSize, setPageSize] = useState(ITEMS_PER_PAGE);
  // Detay masaüstünde sağ panelde durur; dar ekranda ise liste gizlenip detay
  // yerinde açılır. Bu ayrımı CSS ile değil gerçek ekran genişliğiyle yapıyoruz,
  // çünkü dar ekranda otomatik seçim istemiyoruz (aşağıdaki effect'e bak).
  const [isDesktopWidth, setIsDesktopWidth] = useState(() => {
    try {
      return window.matchMedia('(min-width: 1024px)').matches;
    } catch (err) {
      return true;
    }
  });
  useEffect(() => {
    let mq;
    try {
      mq = window.matchMedia('(min-width: 1024px)');
    } catch (err) {
      return undefined;
    }
    const onChange = (e) => setIsDesktopWidth(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  
  // Sub-tab state - İzin Talepleri / Sosyal İzinler / Personel İzin Durumu / İşbaşı Geçmişi
  const [activeSubTab, setActiveSubTab] = useState('requests'); // 'requests' | 'social_leaves' | 'status' | 'return_history'
  const [returnHistory, setReturnHistory] = useState([]);
  const [returnHistoryLoading, setReturnHistoryLoading] = useState(false);
  const [returnHistorySearch, setReturnHistorySearch] = useState('');
  const [returnHistoryEarlyFilter, setReturnHistoryEarlyFilter] = useState('all'); // all|true|false
  const [returnHistoryModeFilter, setReturnHistoryModeFilter] = useState('all'); // all|immediate|end_of_day|custom
  const [returnHistoryFromDate, setReturnHistoryFromDate] = useState('');
  const [returnHistoryToDate, setReturnHistoryToDate] = useState('');

  // Test verilerini temizle dialog
  const [showClearTestDialog, setShowClearTestDialog] = useState(false);
  const [clearTestConfirmText, setClearTestConfirmText] = useState('');
  const [clearTestLoading, setClearTestLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Filtre state'leri - Geçici filtreler (Ara butonuna basılana kadar uygulanmaz)
  const [tempSearchQuery, setTempSearchQuery] = useState('');
  const [tempLeaveTypeFilter, setTempLeaveTypeFilter] = useState('all');
  const [tempStatusFilter, setTempStatusFilter] = useState('all');
  const [tempRegionFilter, setTempRegionFilter] = useState('all');
  const [tempDateFrom, setTempDateFrom] = useState('');
  const [tempDateTo, setTempDateTo] = useState('');
  
  // Aktif filtreler (Ara butonuna basıldıktan sonra uygulanan)
  const [searchQuery, setSearchQuery] = useState('');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showLowLeaveOnly, setShowLowLeaveOnly] = useState(false);
  
  // Personel → bölge id'leri. ÜÇ kaynak sırayla denenir:
  //   1) doğrudan bölge ataması (personnel_region → p.region_ids)
  //   2) formen ise sorumlu olduğu bölge
  //   3) atandığı parkların bölgeleri (park kaydının kendi listesi; ayrı atama
  //      tablosu güvenilmez olduğu için park cache'i kullanılır)
  // 3. kaynak şart: 190 personelin 70'inde doğrudan bölge ataması YOK. Eskiden
  // yalnız 1-2 kullanılıyordu; o 70 kişi hem bölge filtresinde hiç çıkmıyor hem
  // de listede bölgesiz görünüyordu.
  const regionIdsByPersonnelId = useMemo(() => {
    const regionIdsFromParks = new Map();
    (parks || []).forEach((park) => {
      if (!park?.region_id) return;
      (park.assigned_personnel_ids || []).forEach((pid) => {
        if (!regionIdsFromParks.has(pid)) regionIdsFromParks.set(pid, new Set());
        regionIdsFromParks.get(pid).add(park.region_id);
      });
    });

    const map = new Map();
    (personnel || []).forEach((p) => {
      const ids = [];
      const push = (id) => { if (id && !ids.includes(id)) ids.push(id); };
      (Array.isArray(p.region_ids) ? p.region_ids : []).forEach(push);
      if (!ids.length) {
        const formenRegion = (regions || []).find((r) => (r.assigned_formen_ids || []).includes(p.id));
        push(formenRegion?.id);
      }
      if (!ids.length) {
        (regionIdsFromParks.get(p.id) || []).forEach(push);
      }
      if (ids.length) map.set(p.id, ids);
    });
    return map;
  }, [personnel, regions, parks]);

  // Ara butonuna basıldığında filtreleri uygula
  const applyFilters = () => {
    setSearchQuery(tempSearchQuery);
    setLeaveTypeFilter(tempLeaveTypeFilter);
    setStatusFilter(tempStatusFilter);
    setRegionFilter(tempRegionFilter);
    setDateFrom(tempDateFrom);
    setDateTo(tempDateTo);
    setCurrentPage(1);
  };

  // İzin türleri
  const leaveTypes = [
    { type: "Yıllık İzin", icon: "🏖️", color: "bg-blue-500", lightColor: "bg-blue-50", borderColor: "border-blue-300" },
    { type: "Sosyal İzin", icon: "👨‍👩‍👧‍👦", color: "bg-purple-500", lightColor: "bg-purple-50", borderColor: "border-purple-300" },
    { type: "Rapor", icon: "🏥", color: "bg-red-500", lightColor: "bg-red-50", borderColor: "border-red-300" },
    { type: "Diğer", icon: "📋", color: "bg-gray-500", lightColor: "bg-gray-50", borderColor: "border-gray-300" }
  ];

  // İzin türü kartları için baz veriler (izin türü filtresi hariç tüm filtreler uygulanmış)
  // Base requests - SADECE kullanıcının görebileceği talepler (durum filtresi HARİÇ)
  // reason_type bazı kayıtlarda makine slug'ı ("sosyal_izin"/"yillik_izin") olarak
  // yazılıyor (bazı backend akışları _normalize_reason_type'ı atlıyor). Tür kartları,
  // filtre, "Sosyal İzinler" alt sekmesi ve per-talep stil/etiket kanonik etikete
  // ("Sosyal İzin") BİREBİR eşleştiğinden, slug'lı talepler kartlara düşmüyor
  // (ör. Sosyal=0 ama Toplam'da sayılıyor → 2 yıllık + 6 sosyal yerine "Yıllık 2 /
  // Toplam 8"). Veriyi panele girişte tek noktadan kanonikleştir; downstream tüm
  // karşılaştırmalar (ve tür etiketi gösterimi) böylece düzelir.
  const normalizedLeaveRequests = useMemo(
    () => (leaveRequests || []).map(lr => ({
      ...lr,
      reason_type: canonicalizeReasonType(lr?.reason_type || lr?.leave_type),
    })),
    [leaveRequests]
  );

  const baseRequestsForStats = useMemo(() => {
    // Müdür/Başkan(golge_admin) tüm talepleri; MY/BM/Formen ise backend'in
    // scope:"visible" ile kendi hiyerarşik kapsamına (astlarına ait TÜM izinler)
    // sınırladığı listeyi olduğu gibi sayar/gösterir. Eski involvement-only
    // frontend filtresi, backend zaten kapsamla döndürdüğünden astların
    // onaylanan/reddedilen izinlerini panelden/sayaçlardan düşürüyordu (0 hatası).
    // Onay yetkisi hâlâ per-talep current_approver ile ayrık — liste görünürlüğü
    // yetki VERMEZ. Park görevlisi / bilinmeyen rol için involvement filtresi kalır.
    let requests = (isAdmin || isMudur || isGolgeAdmin || isMudurYardimcisi || isBolgeMuhendisi || isFormen)
      ? normalizedLeaveRequests
      : normalizedLeaveRequests.filter(lr =>
          requestViewMode === 'touched'
            ? true
            : (
              lr.personnel_id === personnelId ||
              lr.current_approver_id === personnelId ||
              (lr.approval_chain && lr.approval_chain.includes(personnelId)) ||
              (Array.isArray(lr.delegation_owner_ids) && lr.delegation_owner_ids.includes(personnelId)) ||
              // Özel/istisna değerlendirme: zincir doğrudan MY'ye gittiğinden bypass
              // edilen Formen/BM de bilgi amaçlı görsün. Backend bu talepleri yalnız
              // çağıranın kapsamındaki (astına ait) hâliyle döndürdüğünden, listede
              // gelen her özel/istisna değerlendirme talebi güvenle gösterilebilir.
              (lr.review_mode && lr.review_mode !== 'normal')
            )
        );

    // Personel adı filtresi
    if (searchQuery) {
      const query = normalizeSearchText(searchQuery);
      requests = requests.filter(lr =>
        normalizeSearchText(lr.personnel_name).includes(query)
      );
    }

    // Bölge filtresi — üç kaynaklı harita (park üzerinden türetilenler DAHİL).
    if (regionFilter !== 'all') {
      const personnelInRegion = new Set(
        (personnel || [])
          .filter(p => (regionIdsByPersonnelId.get(p.id) || []).includes(regionFilter))
          .map(p => p.id)
      );
      requests = requests.filter(lr => personnelInRegion.has(lr.personnel_id));
    }

    // Tarih filtresi
    if (dateFrom) {
      requests = requests.filter(lr => new Date(lr.start_date) >= new Date(dateFrom));
    }
    if (dateTo) {
      requests = requests.filter(lr => new Date(lr.end_date) <= new Date(dateTo));
    }

    return requests;
  }, [normalizedLeaveRequests, userRole, personnelId, requestViewMode, searchQuery, regionFilter, dateFrom, dateTo, personnel, regionIdsByPersonnelId]);

  // Base requests with status filter (for type cards)
  const baseRequestsForTypeCards = useMemo(() => {
    let requests = baseRequestsForStats;

    // Durum filtresi
    if (statusFilter !== 'all') {
      if (statusFilter === 'pending') {
        requests = requests.filter(lr => lr.status === 'in_progress');
      } else if (statusFilter === 'mine') {
        // "Bana düşenler": karar bekleyen VE sıradaki onaycı ben.
        requests = requests.filter(lr =>
          lr.status === 'in_progress' &&
          lr.current_approver_id != null &&
          String(lr.current_approver_id) === String(personnelId)
        );
      } else {
        requests = requests.filter(lr => lr.status === statusFilter);
      }
    }

    return requests;
  }, [baseRequestsForStats, statusFilter, personnelId]);

  // Filtrelenmiş talepler (izin türü filtresi dahil)
  const allFilteredRequests = useMemo(() => {
    let requests = baseRequestsForTypeCards;

    // İzin türü filtresi
    if (leaveTypeFilter !== 'all') {
      requests = requests.filter(lr => lr.reason_type === leaveTypeFilter);
    }

    // Sıralama — SAYFALAMADAN ÖNCE, yoksa "bana düşen" kayıt 2. sayfada kalırdı:
    //   1) kararı bende bekleyenler
    //   2) diğer bekleyenler
    //   3) sonuçlananlar
    // Grup içinde mevcut anlamlı sıra (en yeniden en eskiye) korunur.
    const rank = (lr) => {
      if (lr.status !== 'in_progress') return 2;
      const mine = lr.current_approver_id != null &&
        String(lr.current_approver_id) === String(personnelId);
      return mine ? 0 : 1;
    };
    requests = [...requests].sort((a, b) => {
      const diff = rank(a) - rank(b);
      if (diff !== 0) return diff;
      return new Date(b.created_at) - new Date(a.created_at);
    });

    return requests;
  }, [baseRequestsForTypeCards, leaveTypeFilter, personnelId]);

  // Sayfalama — sayfa boyutu kullanıcı seçimi (liste tek parça bellekte olduğu
  // için tamamen istemci tarafında, backend'e ek istek yok).
  const totalPages = Math.max(1, Math.ceil(allFilteredRequests.length / pageSize));
  const paginatedRequests = allFilteredRequests.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Karar listesi: personel → bölge adı. Kartın kendi bölge çözümü yalnız
  // formen/BM/MY için dolu döner (park görevlisinde boş), bu yüzden burada
  // panelin bölge FİLTRESİYLE aynı kaynak kullanılıyor: personnel.region_ids.
  // Özet satırdaki bölge adı — filtrenin kullandığı AYNI haritadan türetilir,
  // böylece "listede bölgesi yazan ama filtrede çıkmayan" tutarsızlık olamaz.
  const regionNameByPersonnelId = useMemo(() => {
    if (!regions.length) return {};
    const regionById = new Map(regions.map((r) => [r.id, r.name]));
    const map = {};
    regionIdsByPersonnelId.forEach((ids, personId) => {
      const names = ids.map((id) => regionById.get(id)).filter(Boolean);
      if (names.length) map[personId] = names.join(', ');
    });
    return map;
  }, [regionIdsByPersonnelId, regions]);

  // Seçili kayıt güncel listeden TÜRETİLİR; sayfa/filtre değişince listede
  // kalmayan bir kaydı sağ panelde tutmayız (spec: eski sayfanın kaydı taşınmaz).
  const selectedRequest = useMemo(
    () => paginatedRequests.find((r) => String(r.id) === String(selectedRequestId)) || null,
    [paginatedRequests, selectedRequestId]
  );

  useEffect(() => {
    if (viewMode !== 'decision') return;
    const stillVisible = paginatedRequests.some((r) => String(r.id) === String(selectedRequestId));
    if (stillVisible) return;
    // Masaüstünde sağ panel boş kalmasın diye sayfanın ilk kaydı seçilir.
    // Dar ekranda otomatik seçim YAPILMAZ; yoksa görünüme geçer geçmez liste
    // kaybolup detay açılırdı. Orada seçim yalnız kullanıcının dokunuşuyla olur.
    if (isDesktopWidth && paginatedRequests.length) {
      setSelectedRequestId(paginatedRequests[0].id);
    } else if (selectedRequestId !== null) {
      setSelectedRequestId(null);
    }
  }, [viewMode, paginatedRequests, selectedRequestId, isDesktopWidth]);

  // Durum kartları için baz: tür filtresi DAHİL, durum filtresi HARİÇ. Böylece
  // üstten bir tür (ör. Yıllık İzin) seçilince alt durum kartları (Toplam/
  // Bekleyen/Onaylanan/Reddedilen) o türe daralır ve kırılım gösterir. Simetrik
  // yapı: tür kartları durum'a (baseRequestsForTypeCards), durum kartları tür'e
  // (bu katman) tepki verir; her ikisinin de kendi filtresi HARİÇ tutulur ki
  // seçim yapınca kendi grubu sıfırlanmasın.
  const baseRequestsForStatusCards = useMemo(() => {
    if (leaveTypeFilter === 'all') return baseRequestsForStats;
    return baseRequestsForStats.filter(lr => lr.reason_type === leaveTypeFilter);
  }, [baseRequestsForStats, leaveTypeFilter]);

  // İstatistikler
  const stats = useMemo(() => {
    // Durum kartları: tür-filtreli (durum filtresi HARİÇ)
    const total = baseRequestsForStatusCards.length;
    const pending = baseRequestsForStatusCards.filter(lr => lr.status === 'in_progress').length;
    const approved = baseRequestsForStatusCards.filter(lr => lr.status === 'approved').length;
    const rejected = baseRequestsForStatusCards.filter(lr => lr.status === 'rejected').length;
    // String karşılaştırma şart: id'ler kimi kayıtta string, kimi kayıtta sayı
    // gelebiliyor; katı === ile sayaç sessizce 0 kalabiliyordu.
    const myPending = baseRequestsForStatusCards.filter(lr =>
      lr.status === 'in_progress' &&
      lr.current_approver_id != null &&
      String(lr.current_approver_id) === String(personnelId)
    ).length;

    // İzin türüne göre sayılar - baseRequestsForTypeCards kullan (durum filtresi DAHİL)
    const byType = leaveTypes.map(lt => ({
      ...lt,
      count: baseRequestsForTypeCards.filter(lr => lr.reason_type === lt.type).length,
      pendingCount: baseRequestsForTypeCards.filter(lr => lr.reason_type === lt.type && lr.status === 'in_progress').length
    }));

    return { total, pending, approved, rejected, myPending, byType };
  }, [baseRequestsForStatusCards, baseRequestsForTypeCards, personnelId]);

  // Faz 4: Müdür panelinde Rapor taleplerini gri bilgi bildirimi olarak göster.
  const reportInfoNotifications = useMemo(() => {
    return baseRequestsForStats
      .filter((lr) => lr.reason_type === 'Rapor' && lr.status === 'in_progress')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 8);
  }, [baseRequestsForStats]);

  // Faz 4: Sosyal izinler için ayrı liste görünümü.
  const socialLeaves = useMemo(() => {
    return baseRequestsForStats
      .filter((lr) => lr.reason_type === 'Sosyal İzin')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [baseRequestsForStats]);

  // CSV Export
  const exportToCSV = () => {
    const headers = ['Personel Adı', 'İzin Türü', 'Başlangıç', 'Bitiş', 'Gün Sayısı', 'Durum', 'Talep Tarihi'];
    const rows = allFilteredRequests.map(lr => [
      lr.personnel_name,
      lr.reason_type || lr.leave_type,
      new Date(lr.start_date).toLocaleDateString('tr-TR'),
      new Date(lr.end_date).toLocaleDateString('tr-TR'),
      lr.total_days,
      lr.status === 'in_progress' ? 'Bekleyen' : lr.status === 'approved' ? 'Onaylanan' : 'Reddedilen',
      new Date(lr.created_at).toLocaleDateString('tr-TR')
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `izin_talepleri_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.csv`;
    link.click();
  };

  // Filtreleri temizle
  const clearFilters = () => {
    setTempSearchQuery('');
    setTempLeaveTypeFilter('all');
    setTempStatusFilter('all');
    setTempRegionFilter('all');
    setTempDateFrom('');
    setTempDateTo('');
    setSearchQuery('');
    setLeaveTypeFilter('all');
    setStatusFilter('all');
    setRegionFilter('all');
    setDateFrom('');
    setDateTo('');
    setShowLowLeaveOnly(false);
    setCurrentPage(1);
  };

  const hasActiveFilters = searchQuery || leaveTypeFilter !== 'all' || statusFilter !== 'all' || 
                          regionFilter !== 'all' || dateFrom || dateTo || showLowLeaveOnly;

  const fetchLeaveReturnHistory = async () => {
    setReturnHistoryLoading(true);
    try {
      const params = {
        from_date: returnHistoryFromDate || undefined,
        to_date: returnHistoryToDate || undefined,
        search: returnHistorySearch || undefined,
        handover_mode: returnHistoryModeFilter !== 'all' ? returnHistoryModeFilter : undefined,
      };
      if (returnHistoryEarlyFilter === 'true') params.returned_early = true;
      if (returnHistoryEarlyFilter === 'false') params.returned_early = false;
      const response = await apiClient.get("/leave-return-history", {
        params,
        timeout: 15000,
      });
      setReturnHistory(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('İşbaşı geçmişi alınamadı:', error);
      // API erişimi geçici olarak başarısızsa, mevcut izin kayıtlarından geçmişi üret.
      const fallbackRows = (leaveRequests || [])
        .filter((lr) => (lr?.actual_return_date || lr?.return_request?.actual_return_date))
        .map((lr) => {
          const rr = lr?.return_request || {};
          const parkSubs = Array.isArray(lr?.park_substitutes) ? lr.park_substitutes : [];
          const uniqueSubIds = new Set(
            parkSubs
              .filter((p) => p && typeof p === "object" && p.substitute_id)
              .map((p) => p.substitute_id)
          );
          const affectedParks = new Set(
            parkSubs
              .filter((p) => p && typeof p === "object" && p.park_id)
              .map((p) => p.park_id)
          );
          if (!uniqueSubIds.size && lr?.substitute_personnel_id) {
            uniqueSubIds.add(lr.substitute_personnel_id);
          }

          const actualReturn = lr?.actual_return_date || rr?.actual_return_date || null;
          const plannedReturn = lr?.return_date || null;
          const returnedEarly = typeof lr?.returned_early === "boolean"
            ? lr.returned_early
            : (!!actualReturn && !!plannedReturn && String(actualReturn) < String(plannedReturn));

          return {
            leave_request_id: lr?.id,
            personnel_id: lr?.personnel_id,
            personnel_name: lr?.personnel_name,
            personnel_role: lr?.personnel_role,
            reason_type: lr?.reason_type,
            start_date: lr?.start_date,
            end_date: lr?.end_date,
            return_date: plannedReturn,
            actual_return_date: actualReturn,
            returned_early: returnedEarly,
            handover_mode: lr?.handover_mode || rr?.handover_mode || null,
            handover_until: lr?.handover_until || rr?.handover_until || null,
            leave_days_refunded: Number(lr?.leave_days_refunded || 0),
            leave_days_used_actual: Number(lr?.leave_days_used_actual || 0),
            return_note: lr?.return_note || rr?.return_note || null,
            return_marked_by_id: lr?.return_marked_by_id || rr?.requested_by_id || null,
            return_marked_by_name: lr?.return_marked_by_name || rr?.requested_by_name || null,
            return_marked_at: lr?.return_marked_at || rr?.requested_at || null,
            status: lr?.status,
            return_request_status: rr?.status,
            substitute_personnel_name: lr?.substitute_personnel_name || null,
            substitute_count: uniqueSubIds.size,
            affected_parks_count: affectedParks.size,
            updated_at: lr?.updated_at || rr?.updated_at || null,
          };
        })
        .sort((a, b) => new Date(b?.updated_at || 0).getTime() - new Date(a?.updated_at || 0).getTime());

      setReturnHistory(fallbackRows);
      if (fallbackRows.length > 0) {
        toast.warning("İşbaşı geçmişi API'den alınamadı, yerel listeden gösteriliyor.");
      } else {
        toast.error(error?.response?.data?.detail || "İşbaşı geçmişi alınamadı");
      }
    } finally {
      setReturnHistoryLoading(false);
    }
  };

  const handleOpenReturnHistoryTab = () => {
    setActiveSubTab('return_history');
    // Sekmeye geçişte isteğin her durumda tetiklenmesini garanti et.
    fetchLeaveReturnHistory();
  };

  const handleReturnHistoryAction = async (row, actionType) => {
    if (isReadOnly) {
      toast.warning("Vekalet verdiğiniz için bu dönemde sadece görüntüleme yapabilirsiniz.");
      return;
    }
    if (!isAdmin && !isMudur) {
      toast.warning("Bu işlem sadece admin veya müdür tarafından yapılabilir.");
      return;
    }
    const requestId = row?.leave_request_id;
    if (!requestId) return;
    const actionLabel = actionType === 'revert' ? 'iade' : actionType === 'cancel' ? 'iptal' : 'sil';
    const ok = window.confirm(`Bu kaydı ${actionLabel} etmek istiyor musunuz?`);
    if (!ok) return;
    try {
      if (actionType === 'delete') {
        try {
          await apiClient.post(
            `/leave-requests/${requestId}/delete?actor_id=${encodeURIComponent(personnelId || "")}`,
            null,
            { timeout: 30000 }
          );
        } catch {
          await apiClient.delete(`/leave-requests/${requestId}?actor_id=${encodeURIComponent(personnelId || "")}`, { timeout: 30000 });
        }
      } else {
        try {
          await apiClient.post(`/leave-requests/${requestId}/return-request/${actionType}?actor_id=${encodeURIComponent(personnelId || "")}`, null, { timeout: 30000 });
        } catch (routeError) {
          // Eski backend sürümlerinde route yoksa test temizliği için silmeye düş.
          if (routeError?.response?.status === 404) {
            try {
              await apiClient.post(
                `/leave-requests/${requestId}/delete?actor_id=${encodeURIComponent(personnelId || "")}`,
                null,
                { timeout: 30000 }
              );
            } catch {
              await apiClient.delete(`/leave-requests/${requestId}?actor_id=${encodeURIComponent(personnelId || "")}`, { timeout: 30000 });
            }
          } else {
            throw routeError;
          }
        }
      }
      toast.success(`İşlem tamamlandı: ${actionLabel}`);
      await fetchLeaveReturnHistory();
      if (onUpdate) await Promise.resolve(onUpdate());
    } catch (error) {
      toast.error(error?.response?.data?.detail || "İşlem başarısız");
    }
  };

  useEffect(() => {
    if (activeSubTab !== 'return_history') return;
    fetchLeaveReturnHistory();
  }, [activeSubTab, personnelId, userRole]);

  // ============ PERSONEL İZİN DURUMU STATE'LERİ ============
  // Geçici filtreler (Ara butonuna basılana kadar uygulanmaz)
  const [tempStatusSearchQuery, setTempStatusSearchQuery] = useState('');
  const [tempStatusRegionFilter, setTempStatusRegionFilter] = useState('all');
  const [tempStatusFormenFilter, setTempStatusFormenFilter] = useState('all');
  const [tempStatusLeaveFilter, setTempStatusLeaveFilter] = useState('all');
  
  // Aktif filtreler
  const [statusSearchQuery, setStatusSearchQuery] = useState('');
  const [statusSortBy, setStatusSortBy] = useState('remaining_asc');
  const [statusRegionFilter, setStatusRegionFilter] = useState('all');
  const [statusFormenFilter, setStatusFormenFilter] = useState('all');
  const [statusLeaveFilter, setStatusLeaveFilter] = useState('all');
  const [statusCurrentPage, setStatusCurrentPage] = useState(1);
  const [statusLeaveTypeSort, setStatusLeaveTypeSort] = useState('all'); // all, annual, social, report, other
  const [regionSearchQuery, setRegionSearchQuery] = useState('');
  const [formenSearchQuery, setFormenSearchQuery] = useState('');
  const STATUS_ITEMS_PER_PAGE = 20;

  const leaveTypeCodeMap = {
    annual: "ANNUAL",
    social: "SOCIAL",
    report: "REPORT",
    other: "OTHER"
  };

  const getSortKeyByLeaveType = (type) => {
    switch (type) {
      case "SOCIAL":
        return "remainingSocial";
      case "ANNUAL":
        return "remainingAnnual";
      case "REPORT":
        return "remainingReport";
      case "OTHER":
        return "remainingOther";
      default:
        return null;
    }
  };
  
  // Personel İzin Durumu - Ara butonuna basıldığında filtreleri uygula
  const applyStatusFilters = () => {
    setStatusSearchQuery(tempStatusSearchQuery);
    setStatusRegionFilter(tempStatusRegionFilter);
    setStatusFormenFilter(tempStatusFormenFilter);
    setStatusLeaveFilter(tempStatusLeaveFilter);
    setStatusCurrentPage(1);
  };
  
  // Personel İzin Durumu - Filtreleri temizle
  const clearStatusFilters = () => {
    setTempStatusSearchQuery('');
    setTempStatusRegionFilter('all');
    setTempStatusFormenFilter('all');
    setTempStatusLeaveFilter('all');
    setStatusSearchQuery('');
    setStatusRegionFilter('all');
    setStatusFormenFilter('all');
    setStatusLeaveFilter('all');
    setStatusLeaveTypeSort('all');
    setStatusSortBy('remaining_asc');
    setStatusCurrentPage(1);
  };

  // Hiyerarşiye göre görülebilir personeller
  const visiblePersonnelForStatus = useMemo(() => {
    // Müdür/Başkan hariç tüm personel (müdür ve golge_admin kendi kendini görmesine gerek yok)
    const tumPersonel = personnel.filter(p => p.role !== 'mudur' && p.role !== 'golge_admin');
    
    // Admin (Müdür) ve Başkan/GKAGÖLGE (golge_admin) – müdür/golge_admin hariç tüm personeli görür
    if (isAdmin || isMudur || isGolgeAdmin) {
      return tumPersonel;
    }
    
    // Müdür Yardımcısı - kendisine bağlı TÜM personelleri görür (doğrudan mudur_yardimcisi_id ile bağlı olanlar)
    if (isMudurYardimcisi) {
      // Doğrudan mudur_yardimcisi_id ile bağlı tüm personeller
      return tumPersonel.filter(p => p.mudur_yardimcisi_id === personnelId);
    }
    
    // Bölge Mühendisi - kendisine bağlı TÜM personelleri görür (doğrudan bolge_muhendisi_id ile bağlı olanlar)
    if (isBolgeMuhendisi) {
      // Doğrudan bolge_muhendisi_id ile bağlı tüm personeller
      return tumPersonel.filter(p => p.bolge_muhendisi_id === personnelId);
    }
    
    // Formen - sadece kendisine bağlı personelleri görür
    if (isFormen) {
      return personnel.filter(p => p.formen_id === personnelId);
    }
    
    return [];
  }, [personnel, userRole, personnelId]);

  // Personel izin durumu filtreleme ve sıralama
  const filteredPersonnelStatus = useMemo(() => {
    const resolveUsedDays = (lr) => {
      const raw = lr?.leave_days_used_actual ?? lr?.workdays_used ?? lr?.total_days;
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) return n;
      const s = lr?.start_date ? String(lr.start_date).slice(0, 10) : null;
      const e = (lr?.end_date || lr?.return_date) ? String(lr.end_date || lr.return_date).slice(0, 10) : s;
      if (!s || !e) return 0;
      const sd = new Date(s);
      const ed = new Date(e);
      if (Number.isNaN(sd.getTime()) || Number.isNaN(ed.getTime())) return 0;
      return Math.max(1, Math.floor((ed - sd) / (1000 * 60 * 60 * 24)) + 1);
    };
    const approvedSocialUsedByPerson = {};
    (leaveRequests || []).forEach((lr) => {
      if (!lr || String(lr.status || '').toLowerCase() !== 'approved') return;
      const reason = String(lr.reason_type || '').toLocaleLowerCase('tr-TR');
      if (!reason.includes('sosyal')) return;
      const pid = lr.personnel_id;
      if (!pid) return;
      approvedSocialUsedByPerson[pid] = (approvedSocialUsedByPerson[pid] || 0) + resolveUsedDays(lr);
    });
    // Yıllık izin kullanımı: saklı sayaç (used_leave_days) onaylı izinde her zaman
    // güncellenmiyor (seed/import kaynaklı kayıtlar sayaç artırmaz). Sosyal'de olduğu
    // gibi onaylı taleplerden de türet; aşağıda Math.max ile birleştir. Aksi halde
    // "Personel İzin Durumu" onaylı yıllık izinleri düşmüyordu (ör. Deniz/Reyfet 7'şer
    // gün onaylı ama Kalan 20/20 görünüyordu). Kanonikleştirme slug'ları (yillik_izin) de yakalar.
    const approvedAnnualUsedByPerson = {};
    (leaveRequests || []).forEach((lr) => {
      if (!lr || String(lr.status || '').toLowerCase() !== 'approved') return;
      if (canonicalizeReasonType(lr.reason_type || lr.leave_type) !== 'Yıllık İzin') return;
      const pid = lr.personnel_id;
      if (!pid) return;
      approvedAnnualUsedByPerson[pid] = (approvedAnnualUsedByPerson[pid] || 0) + resolveUsedDays(lr);
    });

    let filtered = visiblePersonnelForStatus;
    
    if (statusSearchQuery) {
      const q = normalizeSearchText(statusSearchQuery);
      filtered = filtered.filter(p => normalizeSearchText(p.name).includes(q));
    }
    
    if (statusRegionFilter !== 'all') {
      filtered = filtered.filter(p => 
        parks.some(park => park.assigned_personnel_ids?.includes(p.id) && park.region_id === statusRegionFilter)
      );
    }
    
    if (statusFormenFilter !== 'all') {
      filtered = filtered.filter(p => p.formen_id === statusFormenFilter);
    }
    
    if (statusLeaveFilter !== 'all') {
      filtered = filtered.filter(p => {
        // Devir + yıllık izin toplamını hesapla
        const carryover = (p.carryover_days || 0) - (p.used_carryover_days || 0);
        const annual = (p.annual_leave_days || 20) - (p.used_leave_days || 0);
        const total = carryover + annual;
        if (statusLeaveFilter === 'critical') return total <= 5;
        if (statusLeaveFilter === 'low') return total > 5 && total <= 10;
        if (statusLeaveFilter === 'normal') return total > 10;
        return true;
      });
    }
    
    filtered = filtered.map(p => {
      // Devir bilgileri
      const carryover_days = p.carryover_days || 0;
      const used_carryover_days = p.used_carryover_days || 0;
      const remaining_carryover = carryover_days - used_carryover_days;
      
      // Yıllık izin bilgileri — saklı sayaç ile onaylı taleplerden türetilen kullanımı
      // birleştir (sosyal ile aynı desen). Böylece sayaç güncellenmemiş olsa bile
      // onaylı yıllık izinler Kalan'a yansır.
      const annual_leave = p.annual_leave_days || 20;
      const used_leave = Math.max((p.used_leave_days || 0), (approvedAnnualUsedByPerson[p.id] || 0));
      const remaining_annual = annual_leave - used_leave;
      
      // Toplam kullanılabilir yıllık izin
      const total_available = remaining_carryover + remaining_annual;
      
      // Kategori belirleme (sıralama için)
      let category = 3; // Normal (>10)
      if (total_available <= 5) category = 1; // Kritik
      else if (total_available <= 10) category = 2; // Düşük

      const other_count = leaveRequests.filter(lr => lr.personnel_id === p.id && canonicalizeReasonType(lr.reason_type || lr.leave_type) === 'Diğer').length;
      
      return {
        ...p,
        // Devir bilgileri
        carryover_days,
        carryover_from_year: p.carryover_from_year,
        used_carryover_days,
        remaining_carryover,
        // Yıllık izin
        remaining_leave: remaining_annual,
        remainingAnnual: remaining_annual,
        total_available,
        category, // Sıralama için kategori
        // Diğer izinler
        social_used: Math.max((p.used_social_leave_days || 0), (approvedSocialUsedByPerson[p.id] || 0)),
        social_remaining: 12 - Math.max((p.used_social_leave_days || 0), (approvedSocialUsedByPerson[p.id] || 0)),
        report_remaining: 40 - (p.used_report_days || 0),
        other_count,
        remainingSocial: 12 - Math.max((p.used_social_leave_days || 0), (approvedSocialUsedByPerson[p.id] || 0)),
        remainingReport: 40 - (p.used_report_days || 0),
        remainingOther: other_count
      };
    });
    
    // Sıralama mantığı - statusSortBy'a göre
    filtered.sort((a, b) => {
      if (statusSortBy === 'name') {
        return (a.name || '').localeCompare(b.name || '', 'tr');
      }
      const typeCode = leaveTypeCodeMap[statusLeaveTypeSort];
      const typeSortKey = statusLeaveTypeSort !== 'all' ? getSortKeyByLeaveType(typeCode) : null;
      if (typeSortKey) {
        const aVal = a[typeSortKey] ?? 0;
        const bVal = b[typeSortKey] ?? 0;
        if (statusSortBy === 'remaining_desc') {
          if (aVal !== bVal) return bVal - aVal;
        } else if (statusSortBy === 'remaining_asc') {
          if (aVal !== bVal) return aVal - bVal;
        }
        return (a.name || '').localeCompare(b.name || '', 'tr');
      }
      if (statusSortBy === 'remaining_desc') {
        // Çok → Az: Önce çok izni olanlar
        // 1. Önce kategoriye göre TERS sırala (Normal → Düşük → Kritik)
        if (a.category !== b.category) {
          return a.category - b.category; // Normal(3) önce değil, hala kategori sırası
        }
        // 2. Aynı kategoride kalan güne göre AZALAN
        if (a.total_available !== b.total_available) {
          return b.total_available - a.total_available; // Çok önce
        }
        return (a.name || '').localeCompare(b.name || '', 'tr');
      } else {
        // Az → Çok (varsayılan): Önce az izni olanlar
        // 1. Önce kategoriye göre sırala (Kritik → Düşük → Normal)
        if (a.category !== b.category) {
          return a.category - b.category;
        }
        // 2. Aynı kategoride kalan güne göre ARTAN
        if (a.total_available !== b.total_available) {
          return a.total_available - b.total_available; // Az önce
        }
        return (a.name || '').localeCompare(b.name || '', 'tr');
      }
    });
    
    return filtered;
  }, [visiblePersonnelForStatus, parks, statusSearchQuery, statusRegionFilter, statusFormenFilter, statusLeaveFilter, statusSortBy, statusLeaveTypeSort, leaveRequests]);

  const paginatedStatus = filteredPersonnelStatus.slice(
    (statusCurrentPage - 1) * STATUS_ITEMS_PER_PAGE,
    statusCurrentPage * STATUS_ITEMS_PER_PAGE
  );
  const totalStatusPages = Math.ceil(filteredPersonnelStatus.length / STATUS_ITEMS_PER_PAGE);

  const formenList = personnel.filter(p => p.role === 'formen');

  const leaveStatusStats = useMemo(() => {
    const parkGorevlileri = visiblePersonnelForStatus;
    return {
      total: parkGorevlileri.length,
      critical: parkGorevlileri.filter(p => {
        const carryover = (p.carryover_days || 0) - (p.used_carryover_days || 0);
        const annual = (p.annual_leave_days || 20) - (p.used_leave_days || 0);
        return (carryover + annual) <= 5;
      }).length,
      low: parkGorevlileri.filter(p => {
        const carryover = (p.carryover_days || 0) - (p.used_carryover_days || 0);
        const annual = (p.annual_leave_days || 20) - (p.used_leave_days || 0);
        const total = carryover + annual;
        return total > 5 && total <= 10;
      }).length,
      normal: parkGorevlileri.filter(p => {
        const carryover = (p.carryover_days || 0) - (p.used_carryover_days || 0);
        const annual = (p.annual_leave_days || 20) - (p.used_leave_days || 0);
        return (carryover + annual) > 10;
      }).length
    };
  }, [visiblePersonnelForStatus]);

  // Aranabilir bölge listesi
  const filteredRegions = useMemo(() => {
    if (!regionSearchQuery) return regions;
    return regions.filter(r => normalizeSearchText(r.name).includes(normalizeSearchText(regionSearchQuery)));
  }, [regions, regionSearchQuery]);

  // Aranabilir formen listesi
  const filteredFormenList = useMemo(() => {
    let list = formenList;
    if (formenSearchQuery) {
      list = list.filter(f => normalizeSearchText(f.name).includes(normalizeSearchText(formenSearchQuery)));
    }
    return list;
  }, [formenList, formenSearchQuery]);

  const getLeaveColumnClass = (type) => {
    if (statusLeaveTypeSort === 'all') return '';
    return statusLeaveTypeSort === type ? '' : 'opacity-40';
  };

  const exportStatusToCSV = () => {
    const headers = ['Ad Soyad', 'Park', 'Formen', 'Yıllık Hak', 'Kullanılan', 'Kalan Yıllık', 'Kalan Sosyal', 'Kalan Rapor', 'Diğer (Adet)'];
    const rows = filteredPersonnelStatus.map(p => {
      const assignedParks = parks.filter(park => park.assigned_personnel_ids?.includes(p.id)).map(park => park.name).join(', ') || '-';
      const formen = personnel.find(f => f.id === p.formen_id);
      return [p.name, assignedParks, formen?.name || '-', p.annual_leave_days || 20, p.used_leave_days || 0, p.remaining_leave, p.social_remaining, p.report_remaining, p.other_count || 0];
    });
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `personel_izin_durumu_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Sayfa boyutu seçici — HEM üstte (görünüm düğmelerinin yanında) HEM altta
  // gösterilir. 100 seçiliyken yalnız altta olsaydı, seçimi değiştirmek için
  // uzun listenin sonuna inmek gerekirdi.
  const renderPageSizeSelect = (variant) => {
    const isHeader = variant === 'header';
    return (
      <label className={`flex items-center gap-1.5 text-xs ${isHeader ? 'text-white' : 'text-gray-500'}`}>
        <span>Sayfa boyutu</span>
        <select
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            // Boyut değişince eski sayfa numarası aralık dışında kalabilir;
            // güvenli davranış başa dönmek.
            setCurrentPage(1);
          }}
          className={
            isHeader
              ? 'rounded border border-white/40 bg-white/20 px-1.5 py-1 text-xs text-white [&>option]:text-gray-900'
              : 'rounded border border-gray-300 bg-white px-1.5 py-1 text-xs'
          }
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>
      </label>
    );
  };

  // Detay kartı hem KART hem KARAR LİSTESİ görünümünde aynı props ile çizilir.
  // Tek yerde tutulur ki iki görünüm zamanla birbirinden ayrışmasın. Yetki
  // koşulları burada; özet satır hiçbir domain aksiyonu üretmez.
  const renderLeaveCard = (request) => {
    const canApprove = request.current_approver_id != null &&
      String(request.current_approver_id) === String(personnelId);
    const isSocialOrReport = request.reason_type === 'Sosyal İzin' || request.reason_type === 'Rapor';
    return (
      <LeaveRequestCard
        key={`${request.id}-${JSON.stringify(request.park_substitutes || [])}`}
        request={request}
        personnelId={personnelId}
        userRole={userRole}
        filterApprovalHistory={filterApprovalHistory}
        showPersonnelName={true}
        showCancelButton={false}
        onPersonnelClick={handleOpenPersonnelDetailFromRequest}
        showActionButtons={canApprove && !((isAdmin || isMudur) && isSocialOrReport) && request.status === 'in_progress'}
        showDeleteButton={isAdmin || isMudur || (isMudurYardimcisi && isSocialOrReport)}
        showMudurSocialReportNote={(isAdmin || isMudur) && isSocialOrReport}
        mudurSocialReportOverrideEnabled={mudurSocialReportOverrideEnabled}
        isReadOnly={isReadOnly}
        onActionClick={onActionClick}
        onUpdate={onUpdate}
        personnel={personnel}
        parks={parks}
        regions={regions}
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* Ana Başlık ve Tab Seçimi */}
      <div className="flex items-center justify-between flex-wrap gap-4 bg-gradient-to-r from-orange-600 to-red-600 text-white p-4 rounded-lg shadow-lg">
        <div className="flex items-center gap-4">
          <Clock className="h-6 w-6" />
          <h2 className="text-xl font-bold">İZİN YÖNETİMİ</h2>
          <div className="flex bg-white/20 rounded-lg p-1">
            <button
              onClick={() => setActiveSubTab('requests')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                activeSubTab === 'requests' ? 'bg-white text-orange-600' : 'text-white hover:bg-white/10'
              }`}
            >
              📋 İzin Talepleri
            </button>
            <button
              onClick={() => setActiveSubTab('social_leaves')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                activeSubTab === 'social_leaves' ? 'bg-white text-orange-600' : 'text-white hover:bg-white/10'
              }`}
            >
              🟣 Sosyal İzinler
            </button>
            <button
              onClick={() => setActiveSubTab('status')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1 ${
                activeSubTab === 'status' ? 'bg-white text-orange-600' : 'text-white hover:bg-white/10'
              }`}
            >
              👥 Personel İzin Durumu
              {leaveStatusStats.critical > 0 && (
                <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full animate-pulse">
                  {leaveStatusStats.critical}
                </span>
              )}
            </button>
            <button
              onClick={handleOpenReturnHistoryTab}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                activeSubTab === 'return_history' ? 'bg-white text-orange-600' : 'text-white hover:bg-white/10'
              }`}
            >
              🕘 İşbaşı Geçmişi
            </button>
          </div>
        </div>
        
        {activeSubTab === 'requests' && (
          <div className="flex items-center gap-2">
          <div className="flex bg-white/20 rounded-lg p-1">
            <Button
              size="sm"
              variant={requestViewMode === 'inbox' ? 'secondary' : 'ghost'}
              onClick={() => onRequestViewModeChange && onRequestViewModeChange('inbox')}
              className={requestViewMode === 'inbox' ? 'bg-white text-orange-600' : 'text-white hover:bg-white/20'}
            >
              Gelen Talepler
            </Button>
            <Button
              size="sm"
              variant={requestViewMode === 'touched' ? 'secondary' : 'ghost'}
              onClick={() => onRequestViewModeChange && onRequestViewModeChange('touched')}
              className={requestViewMode === 'touched' ? 'bg-white text-orange-600' : 'text-white hover:bg-white/20'}
            >
              İşlem Yaptıklarım
            </Button>
          </div>
          {/* Görünüm Toggle */}
          <div className="flex bg-white/20 rounded-lg p-1">
            <Button
              size="sm"
              variant={viewMode === 'card' ? 'secondary' : 'ghost'}
              onClick={() => changeViewMode('card')}
              title="Kart görünümü"
              aria-label="Kart görünümü"
              className={viewMode === 'card' ? 'bg-white text-orange-600' : 'text-white hover:bg-white/20'}
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'decision' ? 'secondary' : 'ghost'}
              onClick={() => changeViewMode('decision')}
              title="Karar listesi — solda özet, sağda ayrıntı"
              aria-label="Karar listesi görünümü"
              className={viewMode === 'decision' ? 'bg-white text-orange-600' : 'text-white hover:bg-white/20'}
            >
              <Columns className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'table' ? 'secondary' : 'ghost'}
              onClick={() => changeViewMode('table')}
              title="Tablo görünümü"
              aria-label="Tablo görünümü"
              className={viewMode === 'table' ? 'bg-white text-orange-600' : 'text-white hover:bg-white/20'}
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          {renderPageSizeSelect('header')}
          {/* Export */}
          <Button
            size="sm"
            variant="outline"
            onClick={exportToCSV}
            className="bg-white/10 border-white/30 text-white hover:bg-white/20"
          >
            <Download className="h-4 w-4 mr-1" />
            Excel
          </Button>
        </div>
        )}
        
        {activeSubTab === 'status' && (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={exportStatusToCSV}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20"
            >
              <Download className="h-4 w-4 mr-1" />
              Excel
            </Button>
          </div>
        )}
        {activeSubTab === 'social_leaves' && (
          <div className="flex items-center gap-2">
            <Badge className="bg-white/20 text-white border-white/30">
              Toplam {socialLeaves.length} sosyal izin
            </Badge>
          </div>
        )}
        {activeSubTab === 'return_history' && (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={fetchLeaveReturnHistory}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20"
            >
              Yenile
            </Button>
          </div>
        )}
      </div>

      {/* Sadece vekalet nedeniyle salt okunur ise göster; Başkan/GKAGÖLGE için üstte "Admin – Sadece Görüntüleme" var */}
      {isReadOnly && !isGolgeAdmin && (
        <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          Vekalet verdiğiniz için bu dönemde sadece görüntüleme yapabilirsiniz.
        </div>
      )}

      {/* ============ İZİN TALEPLERİ TAB'I ============ */}
      {activeSubTab === 'requests' && (
        <>
          {reportInfoNotifications.length > 0 && (
            <Card className="border-gray-300 bg-gray-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-700 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Rapor Bilgilendirmeleri
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {reportInfoNotifications.map((r) => (
                  <div key={r.id} className="rounded-md border border-gray-300 bg-white p-2">
                    <p className="text-xs text-gray-800">
                      <span className="font-semibold">{r.personnel_name}</span> için rapor talebi
                      ({new Date(r.start_date).toLocaleDateString('tr-TR')} - {new Date(r.end_date).toLocaleDateString('tr-TR')})
                    </p>
                    <p className="text-[11px] text-gray-500 mt-1">
                      Bilgilendirme amaçlıdır, karar mercii MY’dır.
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* İzin Türü Kartları - Modern Tasarım */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.byType.map((lt) => {
          const isSelected = leaveTypeFilter === lt.type;
          return (
            <Card 
              key={lt.type}
              className={`cursor-pointer transition-all duration-200 ${
                isSelected 
                  ? 'ring-4 ring-offset-2 ring-blue-500 shadow-xl scale-[1.03] border-blue-500 bg-blue-50' 
                  : 'hover:shadow-lg hover:scale-[1.02]'
              } ${!isSelected ? lt.lightColor : ''} ${!isSelected ? lt.borderColor : 'border-blue-500'} border-2`}
              onClick={() => {
                setLeaveTypeFilter(isSelected ? 'all' : lt.type);
                setCurrentPage(1);
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-3xl">{lt.icon}</span>
                    <p className={`text-sm font-medium mt-1 ${isSelected ? 'text-blue-800' : 'text-gray-700'}`}>{lt.type}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-2xl font-bold ${isSelected ? 'text-blue-800' : 'text-gray-800'}`}>{lt.count}</p>
                    {lt.pendingCount > 0 && (
                      <Badge className={`text-white text-xs ${isSelected ? 'bg-blue-600' : 'bg-orange-500'}`}>
                        {lt.pendingCount} bekleyen
                      </Badge>
                    )}
                  </div>
                </div>
                {isSelected && (
                  <div className="mt-2 pt-2 border-t border-blue-200 text-center">
                    <span className="text-xs text-blue-600 font-medium">✓ Seçili Filtre</span>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filtreler */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <Filter className="h-4 w-4" />
              Filtreler
            </CardTitle>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Temizle
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-7 gap-3">
            {/* Personel Arama */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Personel adı ara..."
                value={tempSearchQuery}
                onChange={(e) => setTempSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                className="pl-9"
              />
            </div>

            {/* Durum Filtresi */}
            <SearchableSelect
              value={tempStatusFilter}
              onChange={setTempStatusFilter}
              allLabel="Tüm Durumlar"
              allValue="all"
              searchPlaceholder="Durum ara..."
              options={[
                { id: "pending", name: "Bekleyen" },
                { id: "approved", name: "Onaylanan" },
                { id: "rejected", name: "Reddedilen" },
              ]}
            />

            {/* Bölge Filtresi */}
            <SearchableSelect
              value={tempRegionFilter}
              onChange={setTempRegionFilter}
              allLabel="Tüm Bölgeler"
              allValue="all"
              searchPlaceholder="Bölge ara..."
              options={regions.map(r => ({ id: r.id, name: r.name }))}
            />

            {/* Tarih Aralığı Filtresi */}
            <div className="xl:col-span-2">
              <DateRangePicker
                startDate={tempDateFrom}
                endDate={tempDateTo}
                onChange={({ start, end }) => {
                  setTempDateFrom(start);
                  setTempDateTo(end);
                }}
                placeholder="Tarih aralığı"
                highlightHolidays
              />
            </div>

            {/* ARA Butonu */}
            <Button 
              onClick={applyFilters}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              <Search className="h-4 w-4 mr-2" />
              Ara
            </Button>

            {/* Sonuç Sayısı */}
            <div className="flex items-center justify-center bg-gray-100 rounded-md px-3">
              <span className="text-sm text-gray-600">
                <span className="font-bold text-gray-800">{allFilteredRequests.length}</span> sonuç
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* İstatistik Özeti */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {/* "Bana düşenler" — yöneticinin asıl sorusu "benim kararımı bekleyen kaç
            tane?". Sayaç bugüne kadar yalnız Bekleyen kartının içinde küçük bir
            rozetti ve tıklanamıyordu; artık kendi filtresi var. */}
        <Card
          className={`cursor-pointer transition-all duration-200 ${
            statusFilter === 'mine'
              ? 'ring-4 ring-offset-2 ring-indigo-500 shadow-xl scale-[1.03] bg-indigo-50 border-indigo-500'
              : 'hover:shadow-md border-transparent'
          } border-2`}
          onClick={() => { setStatusFilter('mine'); setCurrentPage(1); }}
        >
          <CardContent className="p-4 text-center">
            <UserCheck className="h-6 w-6 mx-auto text-indigo-600 mb-1" />
            <p className="text-2xl font-bold text-indigo-700">{stats.myPending}</p>
            <p className="text-xs text-indigo-600">Bana Düşenler</p>
            {statusFilter === 'mine' && <span className="text-xs text-indigo-500 font-medium">✓ Seçili</span>}
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all duration-200 ${
            statusFilter === 'all'
              ? 'ring-4 ring-offset-2 ring-blue-500 shadow-xl scale-[1.03] bg-blue-50 border-blue-500'
              : 'hover:shadow-md border-transparent'
          } border-2`}
          onClick={() => { setStatusFilter('all'); setCurrentPage(1); }}
        >
          <CardContent className="p-4 text-center">
            <Calendar className="h-6 w-6 mx-auto text-blue-600 mb-1" />
            <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
            <p className="text-xs text-blue-600">Toplam</p>
            {statusFilter === 'all' && <span className="text-xs text-blue-500 font-medium">✓ Seçili</span>}
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all duration-200 ${
            statusFilter === 'pending' 
              ? 'ring-4 ring-offset-2 ring-orange-500 shadow-xl scale-[1.03] bg-orange-50 border-orange-500' 
              : 'hover:shadow-md border-transparent'
          } border-2`}
          onClick={() => { setStatusFilter('pending'); setCurrentPage(1); }}
        >
          <CardContent className="p-4 text-center">
            <Clock className="h-6 w-6 mx-auto text-orange-600 mb-1" />
            <p className="text-2xl font-bold text-orange-700">{stats.pending}</p>
            <p className="text-xs text-orange-600">Bekleyen</p>
            {stats.myPending > 0 && (
              <Badge className="mt-1 bg-orange-500">{stats.myPending} sizde</Badge>
            )}
            {statusFilter === 'pending' && <div className="text-xs text-orange-500 font-medium mt-1">✓ Seçili</div>}
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all duration-200 ${
            statusFilter === 'approved' 
              ? 'ring-4 ring-offset-2 ring-green-500 shadow-xl scale-[1.03] bg-green-50 border-green-500' 
              : 'hover:shadow-md border-transparent'
          } border-2`}
          onClick={() => { setStatusFilter('approved'); setCurrentPage(1); }}
        >
          <CardContent className="p-4 text-center">
            <CheckCircle2 className="h-6 w-6 mx-auto text-green-600 mb-1" />
            <p className="text-2xl font-bold text-green-700">{stats.approved}</p>
            <p className="text-xs text-green-600">Onaylanan</p>
            {statusFilter === 'approved' && <span className="text-xs text-green-500 font-medium">✓ Seçili</span>}
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all duration-200 ${
            statusFilter === 'rejected' 
              ? 'ring-4 ring-offset-2 ring-red-500 shadow-xl scale-[1.03] bg-red-50 border-red-500' 
              : 'hover:shadow-md border-transparent'
          } border-2`}
          onClick={() => { setStatusFilter('rejected'); setCurrentPage(1); }}
        >
          <CardContent className="p-4 text-center">
            <XCircle className="h-6 w-6 mx-auto text-red-600 mb-1" />
            <p className="text-2xl font-bold text-red-700">{stats.rejected}</p>
            <p className="text-xs text-red-600">Reddedilen</p>
            {statusFilter === 'rejected' && <span className="text-xs text-red-500 font-medium">✓ Seçili</span>}
          </CardContent>
        </Card>
      </div>

      {/* İçerik */}
      {paginatedRequests.length === 0 ? (
        <Card className="p-12 text-center">
          <Clock className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500 text-lg">
            {hasActiveFilters ? 'Filtrelere uygun izin talebi bulunamadı.' : 'Henüz izin talebi yok.'}
          </p>
          {hasActiveFilters && (
            <Button variant="outline" onClick={clearFilters} className="mt-4">
              Filtreleri Temizle
            </Button>
          )}
        </Card>
      ) : viewMode === 'decision' ? (
        /* KARAR LİSTESİ (master-detail): solda özet satırlar, sağda MEVCUT kart.
           Sağ panelde yeni bir detay arayüzü YAZILMAZ — aynı LeaveRequestCard
           çizilir, böylece yetki/onay/vekil/silme davranışı birebir korunur. */
        <>
          {/* Dar ekranda seçim yapılınca liste GİZLENİR, detay tam genişlikte
              yerinde açılır ("master VEYA detail"). Önce alttan açılan bir panel
              (Sheet) kullanıyordum; o bir katman penceresi olduğu için onay/silme
              gibi diğer pencerelerle üst üste biniyor ve onay penceresi
              görünmüyordu. Yerinde açılan detayda böyle bir çakışma yok. */}
          {!isDesktopWidth && selectedRequest && (
            <div className="mb-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedRequestId(null)}
                className="mb-3"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Listeye dön
              </Button>
              {renderLeaveCard(selectedRequest)}
            </div>
          )}

          <div
            className={`grid grid-cols-1 gap-4 lg:grid-cols-[380px_minmax(0,1fr)] xl:grid-cols-[430px_minmax(0,1fr)] ${
              !isDesktopWidth && selectedRequest ? 'hidden' : ''
            }`}
          >
            <div className="flex flex-col gap-2">
              {paginatedRequests.map(request => (
                <LeaveRequestSummaryRow
                  key={request.id}
                  request={request}
                  regionName={regionNameByPersonnelId[request.personnel_id] || null}
                  isSelected={String(request.id) === String(selectedRequestId)}
                  awaitingMyDecision={
                    request.status === 'in_progress' &&
                    request.current_approver_id != null &&
                    String(request.current_approver_id) === String(personnelId)
                  }
                  onSelect={setSelectedRequestId}
                />
              ))}
            </div>

            {/* lg ve üzeri: sağ detay paneli. STICKY + kendi içinde kaydırılır:
                (1) uzun kart sayfayı aşağı uzatmaz, kendi içinde kayar;
                (2) listede aşağı inip bir kayda tıklayınca detay göz hizasında
                    kalır — kullanıcı her seçimde başa dönmek zorunda kalmaz. */}
            <div className="hidden lg:block">
              {/* Not: kaydırma kabı pencere DEĞİL, App.js'teki
                  `.flex-1.overflow-auto` (yüksekliği 100vh - header). sticky top
                  bu kabın üstünden ölçülür; header payı EKLENMEZ, yoksa panel
                  bir header boyu aşağı kayardı. */}
              <div className="sticky top-4 max-h-[calc(100vh-var(--app-header-h)-2rem)] overflow-y-auto pr-1">
                {selectedRequest ? (
                  renderLeaveCard(selectedRequest)
                ) : (
                  <Card className="p-8 text-center text-gray-500">
                    Ayrıntıyı görmek için soldan bir talep seçin.
                  </Card>
                )}
              </div>
            </div>
          </div>

        </>
      ) : viewMode === 'table' ? (
        /* TABLO GÖRÜNÜMÜ */
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-semibold">Personel</TableHead>
                    <TableHead className="font-semibold">Kalan İzin (Devir + Bu Yıl)</TableHead>
                    <TableHead className="font-semibold">İzin Türü</TableHead>
                    <TableHead className="font-semibold">Tarih Aralığı</TableHead>
                    <TableHead className="font-semibold text-center">Gün</TableHead>
                    <TableHead className="font-semibold">Durum</TableHead>
                    <TableHead className="font-semibold">Onay Geçmişi</TableHead>
                    <TableHead className="font-semibold text-right">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedRequests.map(request => {
                    const person = personnel.find(p => p.id === request.personnel_id);
                    const currentApproverPerson = getCurrentApproverPerson(request, personnel);
                    const isSocialReport = request.reason_type === 'Sosyal İzin' || request.reason_type === 'Rapor';
                    const showMudurNote = (isAdmin || isMudur) && isSocialReport;
                    
                    // Devir bilgileri
                    const carryover = person ? (person.carryover_days || 0) - (person.used_carryover_days || 0) : 0;
                    const annual = person ? (person.annual_leave_days || 20) - (person.used_leave_days || 0) : 0;
                    const totalAvailable = carryover + annual;
                    
                    const canApprove = request.current_approver_id != null &&
                      String(request.current_approver_id) === String(personnelId);
                    const myChainIndex = request.approval_chain?.findIndex(id => String(id) === String(personnelId)) ?? -1;
                    const delegatedFromId = request.current_approver_delegated_from_id != null
                      ? String(request.current_approver_delegated_from_id)
                      : "";
                    const delegatedChainIndex = delegatedFromId
                      ? (request.approval_chain?.findIndex(id => String(id) === delegatedFromId) ?? -1)
                      : -1;
                    const effectiveChainIndex = myChainIndex >= 0 ? myChainIndex : delegatedChainIndex;
                    const canEscalate = canApprove &&
                      effectiveChainIndex >= 0 &&
                      effectiveChainIndex < ((request.approval_chain?.length ?? 0) - 1);
                    const actionBlockedByReadOnly =
                      isReadOnly &&
                      !canApprove &&
                      delegatedFromId !== String(personnelId ?? "");
                    const statusColors = {
                      'in_progress': 'bg-orange-100 text-orange-800',
                      'approved': 'bg-green-100 text-green-800',
                      'rejected': 'bg-red-100 text-red-800'
                    };
                    const statusLabels = {
                      'in_progress': 'Bekleyen',
                      'approved': 'Onaylanan',
                      'rejected': 'Reddedilen'
                    };
                    const typeConfig = leaveTypes.find(lt => lt.type === request.reason_type) || leaveTypes[3];
                    const disableMudurActions = false;

                    return (
                      <TableRow key={request.id} className="hover:bg-gray-50">
                        <TableCell>
                          {handleOpenPersonnelDetailFromRequest ? (
                            <button
                              type="button"
                              onClick={() => handleOpenPersonnelDetailFromRequest(request)}
                              title="Personel izin durumunu aç"
                              className="font-medium text-left text-blue-700 hover:text-blue-900 hover:underline underline-offset-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                            >
                              {request.personnel_name}
                            </button>
                          ) : (
                            <div className="font-medium">{request.personnel_name}</div>
                          )}
                          <div className="text-xs text-gray-500">{person?.custom_role_name || ''}</div>
                        </TableCell>
                        <TableCell>
                          {request.reason_type === 'Yıllık İzin' && person ? (
                            <div className="flex flex-col gap-1">
                              <Badge 
                                variant="outline" 
                                className={totalAvailable <= 5 ? 'bg-red-50 text-red-700 border-red-300' : 'bg-green-50 text-green-700 border-green-300'}
                              >
                                {totalAvailable} gün
                              </Badge>
                              <div className="flex gap-1 text-xs">
                                {carryover > 0 && (
                                  <span className="text-amber-600" title={`${person.carryover_from_year}'den devir`}>📦{carryover}</span>
                                )}
                                <span className="text-blue-600">📅{annual}</span>
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${typeConfig.lightColor} ${typeConfig.borderColor} border`}>
                            <span>{typeConfig.icon}</span>
                            <span className="text-gray-800">{request.reason_type || request.leave_type}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {new Date(request.start_date).toLocaleDateString('tr-TR')}
                            <span className="mx-1">-</span>
                            {new Date(request.end_date).toLocaleDateString('tr-TR')}
                          </div>
                        </TableCell>
                        <TableCell className="text-center font-semibold">
                          {request.total_days}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge className={showMudurNote && request.status === 'in_progress'
                              ? 'bg-gray-200 text-gray-700'
                              : statusColors[request.status]}>
                              {statusLabels[request.status]}
                            </Badge>
                            {request.status === 'in_progress' && currentApproverPerson?.name && (
                              <div className="text-[10px] leading-3 font-semibold text-gray-700">
                                İncelemede: {getApproverRoleLabel(currentApproverPerson.role)} {currentApproverPerson.name}
                              </div>
                            )}
                    {showMudurNote && request.status === 'in_progress' && (
                              <div className="text-[10px] leading-3 text-gray-500 italic">
                                Karar varsayılan olarak MY’da
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Popover>
                            <PopoverTrigger asChild>
                              <div className="flex flex-wrap gap-1 max-w-[200px] cursor-pointer hover:bg-gray-100 p-1 rounded">
                                {request.approval_history?.slice(0, 3).map((h, i) => (
                                  <Badge 
                                    key={i} 
                                    variant="outline" 
                                    className={`text-xs ${h.action === 'approved' ? 'bg-green-50 text-green-700' : h.action === 'rejected' ? 'bg-red-50 text-red-700' : 'bg-gray-50'}`}
                                  >
                                    {h.approver_name?.split(' ')[0]}
                                  </Badge>
                                ))}
                                {request.approval_history?.length > 3 && (
                                  <Badge variant="outline" className="text-xs">+{request.approval_history.length - 3}</Badge>
                                )}
                                {request.approval_history?.some(h => h.comment) && (
                                  <MessageSquare className="h-3 w-3 text-blue-500 ml-1" />
                                )}
                              </div>
                            </PopoverTrigger>
                            <PopoverContent className="w-80 p-0" align="start">
                              <div className="p-3 border-b bg-gray-50">
                                <p className="font-semibold text-sm">Onay Geçmişi</p>
                              </div>
                              <div className="max-h-64 overflow-y-auto">
                                {request.approval_history?.map((h, i) => {
                                  // Karar türünü belirle
                                  const hasRejectionComment = h.comment && h.comment.startsWith("RED ÖNERİSİ:");
                                  const isApproval = h.action === 'approved' || h.action === 'approve';
                                  const isRejection = h.action === 'rejected' || h.action === 'reject';
                                  const isRejectAndForward = h.action === 'reject_and_forward';  // Reddip üst makama sevk
                                  const isForward = h.action === 'forward' || h.action === 'escalate';
                                  
                                  // Onaylayan kişinin rolünü bul
                                  const approverPerson = personnel.find(p => p.id === h.approver_id);
                                  
                                  // İzin türüne göre son onay makamını belirle
                                  // Yıllık İzin: Son onay = Müdür (admin)
                                  // Sosyal İzin, Rapor: Son onay = Müdür Yardımcısı
                                  const leaveType = request.reason_type;
                                  let isFinalApprover = false;
                                  if (leaveType === 'Yıllık İzin') {
                                    // Yıllık izinde sadece Müdür son onay makamı
                                    isFinalApprover = approverPerson?.role === 'mudur';
                                  } else {
                                    // Sosyal İzin, Rapor, Diğer için Müdür Yardımcısı veya Müdür son onay makamı
                                    isFinalApprover = approverPerson?.role === 'mudur_yardimcisi' || approverPerson?.role === 'mudur';
                                  }
                                  
                                  // Gerçek yorumu çıkar (otomatik prefix'leri kaldır)
                                  let realComment = null;
                                  if (h.comment) {
                                    let cleanComment = h.comment;
                                    // RED ÖNERİSİ: prefix'ini kaldır
                                    if (cleanComment.startsWith("RED ÖNERİSİ:")) {
                                      cleanComment = cleanComment.replace(/^RED ÖNERİSİ:\s*/, '');
                                    }
                                    // Otomatik yorumları filtrele
                                    if (cleanComment && 
                                        !cleanComment.startsWith("Approved by") && 
                                        !cleanComment.startsWith("Escalating to") &&
                                        cleanComment.trim().length > 0) {
                                      realComment = cleanComment;
                                    }
                                  }
                                  
                                  let decisionText = "";
                                  let bgColor = "";
                                  let textColor = "";
                                  
                                  if (isApproval && !isForward) {
                                    // Müdür Yardımcısı ise son onay makamı - sadece "Onayladı" yaz
                                    if (isFinalApprover) {
                                      decisionText = "✅ Onayladı";
                                    } else {
                                      decisionText = "✅ Onaylayarak Üst Makama Sevk Etti";
                                    }
                                    bgColor = "bg-green-50";
                                    textColor = "text-green-700";
                                  } else if (isRejectAndForward || hasRejectionComment) {
                                    decisionText = "❌ Reddet ile Üst Makama Sevk Etti.";
                                    bgColor = "bg-red-50";
                                    textColor = "text-red-700";
                                  } else if (isRejection) {
                                    decisionText = "❌ Reddetti";
                                    bgColor = "bg-red-50";
                                    textColor = "text-red-700";
                                  } else if (isForward) {
                                    decisionText = "⬆️ Üst Makama Sevk Etti";
                                    bgColor = "bg-blue-50";
                                    textColor = "text-blue-700";
                                  } else {
                                    decisionText = "⬆️ Üst Makama Sevk Etti";
                                    bgColor = "bg-blue-50";
                                    textColor = "text-blue-700";
                                  }
                                  
                                  return (
                                    <div key={i} className={`p-3 border-b last:border-b-0 ${bgColor}`}>
                                      <div className="flex items-center justify-between">
                                        <span className="font-medium text-sm">{h.approver_name}</span>
                                        <span className="text-[10px] text-gray-500">
                                          {new Date(h.timestamp).toLocaleString('tr-TR')}
                                        </span>
                                      </div>
                                      <p className={`text-xs font-semibold mt-1 ${textColor}`}>
                                        {decisionText}
                                      </p>
                                      {h.delegated_from_name && (
                                        <p className="text-[11px] text-amber-700 mt-1">
                                          Vekalet veren: {getApproverRoleLabel(h.delegated_from_role)} {h.delegated_from_name}
                                        </p>
                                      )}
                                      {realComment && (
                                        <p className="text-xs text-gray-600 bg-white p-2 rounded mt-2 border-l-2 border-gray-300">
                                          {realComment}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </PopoverContent>
                          </Popover>
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          {(canApprove && !((isAdmin || isMudur) && isSocialReport)) && request.status === 'in_progress' && (
                            <div className="flex gap-1 justify-end">
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 bg-green-50 hover:bg-green-100 text-green-700"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  if (onActionClick) onActionClick(request, 'approve');
                                }}
                                disabled={actionBlockedByReadOnly}
                                title="Onayla"
                                aria-label="Onayla"
                              >
                                <CheckCircle2 className="h-3 w-3" />
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 bg-red-50 hover:bg-red-100 text-red-700"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  if (onActionClick) onActionClick(request, 'reject');
                                }}
                                disabled={actionBlockedByReadOnly}
                                title="Reddet"
                                aria-label="Reddet"
                              >
                                <XCircle className="h-3 w-3" />
                              </Button>
                              {canEscalate && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    if (onActionClick) onActionClick(request, 'escalate');
                                  }}
                                  disabled={actionBlockedByReadOnly}
                                  title="Üst makama sevk et"
                                  aria-label="Üst makama sevk et"
                                >
                                  ⬆️ Üst Makama
                                </Button>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* KART GÖRÜNÜMÜ */
        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
          {/* Kart props'ları renderLeaveCard'da tek yerde — karar listesiyle aynı
              kaynak, iki görünüm zamanla ayrışmasın. Faz 0: dış Card'ın çerçevesi
              kaldırıldı, LeaveRequestCard zaten kendi border+p-4'ünü çiziyor. */}
          {paginatedRequests.map(request => (
            <Card key={request.id} className="border-0 shadow-none bg-transparent overflow-hidden">
              <CardContent className="p-0">
                {renderLeaveCard(request)}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Yüklenen kayıt sınırı — liste 300'e dayandıysa daha fazlası OLABİLİR ama
          bunu bilemeyiz. "İlk 300 gösteriliyor" demek kesinlik iddia etmek olurdu;
          bu yüzden bilgilendirici ton, kırmızı hata değil. */}
      {(leaveRequests || []).length >= LOADED_LIMIT && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
          {LOADED_LIMIT} kayıt yüklendi. Daha fazla kayıt bulunuyor olabilir; sayaçlar ve
          filtreler yüklenen bu liste üzerinden hesaplanır. Aradığınız kayıt görünmüyorsa
          filtreleri daraltın (tarih aralığı, bölge veya personel adı).
        </div>
      )}

      {/* Sayfalama — kayıt sayısı ve sayfa boyutu HER ZAMAN görünür (tek sayfalık
          listede de kaç kayıt olduğunu görmek işe yarıyor). Sayfa numarası ve
          ileri/geri düğmeleri yalnız birden fazla sayfa varsa çizilir. */}
      {allFilteredRequests.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <span>
              {totalPages > 1 && <>Sayfa {currentPage} / {totalPages}</>}
              <span className={totalPages > 1 ? 'ml-2' : ''}>({allFilteredRequests.length} kayıt)</span>
            </span>
            {renderPageSizeSelect('footer')}
          </div>
          <div className={`flex items-center gap-2 ${totalPages > 1 ? '' : 'hidden'}`}>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Önceki
            </Button>
            
            {/* Sayfa numaraları */}
            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className="w-8 h-8 p-0"
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Sonraki
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
        </>
      )}

      {activeSubTab === 'social_leaves' && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">🟣 Sosyal İzinler</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <p className="text-sm text-gray-600">
                Müdür sosyal izin hiyerarşi akışını bu listeden takip edebilir ve gerekirse kaydı silebilir.
              </p>
            </CardContent>
          </Card>

          {socialLeaves.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">Sosyal izin kaydı bulunamadı.</CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
              {socialLeaves.map((request) => {
                const canApprove = request.current_approver_id != null &&
                  String(request.current_approver_id) === String(personnelId);

                return (
                  <Card key={request.id} className="border-0 shadow-none bg-transparent overflow-hidden">
                    <CardContent className="p-0">
                      <LeaveRequestCard
                        key={`${request.id}-${JSON.stringify(request.park_substitutes || [])}`}
                        request={request}
                        personnelId={personnelId}
                        userRole={userRole}
                        filterApprovalHistory={filterApprovalHistory}
                        showPersonnelName={true}
                        showCancelButton={false}
                        onPersonnelClick={handleOpenPersonnelDetailFromRequest}
                        showActionButtons={canApprove && request.status === 'in_progress'}
                        showDeleteButton={isAdmin || isMudur || (isMudurYardimcisi && (request.reason_type === 'Sosyal İzin' || request.reason_type === 'Rapor'))}
                        showMudurSocialReportNote={(isAdmin || isMudur) && (request.reason_type === 'Sosyal İzin' || request.reason_type === 'Rapor')}
                        mudurSocialReportOverrideEnabled={mudurSocialReportOverrideEnabled}
                        isReadOnly={isReadOnly}
                        onActionClick={onActionClick}
                        onUpdate={onUpdate}
                        personnel={personnel}
                        parks={parks}
                        regions={regions}
                      />
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ============ PERSONEL İZİN DURUMU TAB'I ============ */}
      {activeSubTab === 'status' && (
        <div className="space-y-4">
          {/* Özet İstatistikler */}
          <div className="grid grid-cols-4 gap-4">
            <Card 
              className={`cursor-pointer transition-all ${statusLeaveFilter === 'all' ? 'ring-2 ring-blue-500' : ''}`}
              onClick={() => { setStatusLeaveFilter('all'); setStatusCurrentPage(1); }}
            >
              <CardContent className="p-4 text-center">
                <Users className="h-8 w-8 mx-auto text-blue-500 mb-2" />
                <p className="text-2xl font-bold">{leaveStatusStats.total}</p>
                <p className="text-xs text-gray-500">Toplam Personel</p>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer transition-all bg-red-50 border-red-200 ${statusLeaveFilter === 'critical' ? 'ring-2 ring-red-500' : ''}`}
              onClick={() => { setStatusLeaveFilter(statusLeaveFilter === 'critical' ? 'all' : 'critical'); setStatusCurrentPage(1); }}
            >
              <CardContent className="p-4 text-center">
                <AlertTriangle className="h-8 w-8 mx-auto text-red-500 mb-2" />
                <p className="text-2xl font-bold text-red-600">{leaveStatusStats.critical}</p>
                <p className="text-xs text-red-600">Kritik (≤5 gün)</p>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer transition-all bg-orange-50 border-orange-200 ${statusLeaveFilter === 'low' ? 'ring-2 ring-orange-500' : ''}`}
              onClick={() => { setStatusLeaveFilter(statusLeaveFilter === 'low' ? 'all' : 'low'); setStatusCurrentPage(1); }}
            >
              <CardContent className="p-4 text-center">
                <Clock className="h-8 w-8 mx-auto text-orange-500 mb-2" />
                <p className="text-2xl font-bold text-orange-600">{leaveStatusStats.low}</p>
                <p className="text-xs text-orange-600">Düşük (6-10 gün)</p>
              </CardContent>
            </Card>
            <Card 
              className={`cursor-pointer transition-all bg-green-50 border-green-200 ${statusLeaveFilter === 'normal' ? 'ring-2 ring-green-500' : ''}`}
              onClick={() => { setStatusLeaveFilter(statusLeaveFilter === 'normal' ? 'all' : 'normal'); setStatusCurrentPage(1); }}
            >
              <CardContent className="p-4 text-center">
                <CheckCircle2 className="h-8 w-8 mx-auto text-green-500 mb-2" />
                <p className="text-2xl font-bold text-green-600">{leaveStatusStats.normal}</p>
                <p className="text-xs text-green-600">Normal (&gt;10 gün)</p>
              </CardContent>
            </Card>
          </div>

          {/* Filtreler */}
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                {/* Arama */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Personel ara..."
                    value={tempStatusSearchQuery}
                    onChange={(e) => setTempStatusSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && applyStatusFilters()}
                    className="pl-10"
                  />
                  {tempStatusSearchQuery && (
                    <button 
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setTempStatusSearchQuery('')}
                    >
                      ✕
                    </button>
                  )}
                </div>
                
                {/* Bölge - aranabilir */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between min-w-0">
                      <span className="truncate">
                        {tempStatusRegionFilter === 'all'
                          ? 'Tüm Bölgeler'
                          : regions.find(r => r.id === tempStatusRegionFilter)?.name || 'Bölge seçin'}
                      </span>
                      <ChevronRight className="h-4 w-4 opacity-50 rotate-90 flex-shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-2">
                    <Input
                      placeholder="Bölge ara..."
                      value={regionSearchQuery}
                      onChange={(e) => setRegionSearchQuery(e.target.value)}
                      className="mb-2"
                    />
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      <button
                        className={`w-full text-left px-2 py-1.5 rounded text-sm hover:bg-gray-100 ${tempStatusRegionFilter === 'all' ? 'bg-blue-50 text-blue-700' : ''}`}
                        onClick={() => { setTempStatusRegionFilter('all'); setRegionSearchQuery(''); }}
                      >
                        Tüm Bölgeler
                      </button>
                      {filteredRegions.map(r => (
                        <button
                          key={r.id}
                          className={`w-full text-left px-2 py-1.5 rounded text-sm hover:bg-gray-100 ${tempStatusRegionFilter === r.id ? 'bg-blue-50 text-blue-700' : ''}`}
                          onClick={() => { setTempStatusRegionFilter(r.id); setRegionSearchQuery(''); }}
                        >
                          {r.name}
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                
                {/* Formen - aranabilir */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between min-w-0">
                      <span className="truncate">
                        {tempStatusFormenFilter === 'all'
                          ? 'Tüm Formenler'
                          : formenList.find(f => f.id === tempStatusFormenFilter)?.name || 'Formen seçin'}
                      </span>
                      <ChevronRight className="h-4 w-4 opacity-50 rotate-90 flex-shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-2">
                    <Input
                      placeholder="Formen ara..."
                      value={formenSearchQuery}
                      onChange={(e) => setFormenSearchQuery(e.target.value)}
                      className="mb-2"
                    />
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      <button
                        className={`w-full text-left px-2 py-1.5 rounded text-sm hover:bg-gray-100 ${tempStatusFormenFilter === 'all' ? 'bg-blue-50 text-blue-700' : ''}`}
                        onClick={() => { setTempStatusFormenFilter('all'); setFormenSearchQuery(''); }}
                      >
                        Tüm Formenler
                      </button>
                      {filteredFormenList.map(f => (
                        <button
                          key={f.id}
                          className={`w-full text-left px-2 py-1.5 rounded text-sm hover:bg-gray-100 ${tempStatusFormenFilter === f.id ? 'bg-blue-50 text-blue-700' : ''}`}
                          onClick={() => { setTempStatusFormenFilter(f.id); setFormenSearchQuery(''); }}
                        >
                          {f.name}
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>

                {/* İzin Türü */}
                <SearchableSelect
                  value={statusLeaveTypeSort}
                  onChange={setStatusLeaveTypeSort}
                  allLabel="Tüm İzinler"
                  allValue="all"
                  searchPlaceholder="İzin türü ara..."
                  options={[
                    { id: "annual", name: "Yıllık İzin" },
                    { id: "social", name: "Sosyal İzin" },
                    { id: "report", name: "Rapor" },
                    { id: "other", name: "Diğer" },
                  ]}
                />

                {/* Sıralama */}
                <SearchableSelect
                  value={statusSortBy}
                  onChange={setStatusSortBy}
                  showAllOption={false}
                  placeholder="Sırala"
                  searchPlaceholder="Sırala..."
                  options={[
                    { id: "remaining_asc", name: "Kalan (Az → Çok)" },
                    { id: "remaining_desc", name: "Kalan (Çok → Az)" },
                    { id: "name", name: "İsme Göre (A-Z)" },
                  ]}
                />
              </div>
              
              {/* Ara, Temizle ve Test verilerini temizle Butonları */}
              <div className="flex gap-2 mt-4 justify-end flex-wrap">
                <Button 
                  onClick={applyStatusFilters}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Ara
                </Button>
                <Button 
                  variant="outline" 
                  onClick={clearStatusFilters}
                >
                  <Filter className="h-4 w-4 mr-1" />
                  Temizle
                </Button>
                {typeof onClearLeaveTestData === 'function' && (
                  <Button
                    variant="outline"
                    className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
                      onClick={() => { if (!isReadOnly) { setShowClearTestDialog(true); setClearTestConfirmText(''); } }}
                      disabled={isReadOnly}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Test verilerini temizle
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Test verilerini temizle onay dialog */}
          <Dialog open={showClearTestDialog} onOpenChange={setShowClearTestDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="h-5 w-5" />
                  İzin test verilerini temizle
                </DialogTitle>
              </DialogHeader>
              <p className="text-sm text-gray-600">
                Bu işlem <strong>tüm izin taleplerini</strong> silecek ve <strong>tüm personelin izin sayaçlarını</strong> (yıllık, sosyal, rapor) sıfırlayacak. Geri alınamaz.
              </p>
              <div className="space-y-2">
                <Label htmlFor="clear-test-confirm">Onaylamak için aşağıya <span className="font-mono bg-red-100 px-1 rounded">SIFIRLA</span> yazın:</Label>
                <Input
                  id="clear-test-confirm"
                  placeholder="SIFIRLA"
                  value={clearTestConfirmText}
                  onChange={(e) => setClearTestConfirmText(e.target.value)}
                  className="font-mono"
                  autoComplete="off"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowClearTestDialog(false)} disabled={clearTestLoading}>
                  İptal
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={clearTestConfirmText !== 'SIFIRLA' || clearTestLoading}
                  onClick={async () => {
                    if (clearTestConfirmText !== 'SIFIRLA') return;
                    setClearTestLoading(true);
                    try {
                      await onClearLeaveTestData(clearTestConfirmText);
                      setShowClearTestDialog(false);
                      setClearTestConfirmText('');
                    } catch (_) {
                      // Hata toast ile gösterilir (parent'ta)
                    } finally {
                      setClearTestLoading(false);
                    }
                  }}
                >
                  {clearTestLoading ? 'Temizleniyor...' : 'Temizle'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Sonuç Sayısı */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              {filteredPersonnelStatus.length} personel gösteriliyor
              {statusLeaveFilter !== 'all' && ` (${statusLeaveFilter === 'critical' ? 'Kritik' : statusLeaveFilter === 'low' ? 'Düşük' : 'Normal'} filtresi aktif)`}
            </p>
          </div>

          {/* Personel Tablosu */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold">Personel</TableHead>
                  <TableHead className="font-semibold">Park</TableHead>
                  <TableHead className="font-semibold">Formen</TableHead>
                  <TableHead className={`font-semibold text-center ${getLeaveColumnClass('annual')}`}>
                    <div className="flex flex-col items-center">
                      <span>Yıllık İzin</span>
                      <span className="text-xs font-normal text-gray-500">(Devir + Bu Yıl)</span>
                    </div>
                  </TableHead>
                  <TableHead className={`font-semibold text-center ${getLeaveColumnClass('social')}`}>Sosyal İzin</TableHead>
                  <TableHead className={`font-semibold text-center ${getLeaveColumnClass('report')}`}>Rapor</TableHead>
                  <TableHead className={`font-semibold text-center ${getLeaveColumnClass('other')}`}>Diğer</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedStatus.map(person => {
                  const assignedParks = parks.filter(park => park.assigned_personnel_ids?.includes(person.id));
                  const formen = personnel.find(f => f.id === person.formen_id);
                  
                  return (
                    <TableRow 
                      key={person.id} 
                      className="hover:bg-blue-50 cursor-pointer transition-colors"
                      onClick={() => !isReadOnly && onEditPersonnel && onEditPersonnel(person)}
                      title="İzin detayları için tıklayın"
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                            person.total_available <= 5 ? 'bg-red-500' :
                            person.total_available <= 10 ? 'bg-orange-500' : 'bg-green-500'
                          }`}>
                            {person.name?.charAt(0)}
                          </div>
                          <div>
                            <span className="font-medium">{person.name}</span>
                            <p className="text-xs text-blue-500">İzin detayı için tıkla →</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {assignedParks.length > 0 
                          ? assignedParks.map(p => p.name).join(', ').substring(0, 40) + (assignedParks.map(p => p.name).join(', ').length > 40 ? '...' : '')
                          : <span className="text-gray-400">-</span>
                        }
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">{formen?.name || '-'}</TableCell>
                      <TableCell className={`text-center ${getLeaveColumnClass('annual')}`}>
                        <div className="flex flex-col items-center gap-1">
                          {/* Toplam kullanılabilir */}
                          <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-semibold ${
                            person.total_available <= 5 ? 'bg-red-100 text-red-700' :
                            person.total_available <= 10 ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {person.total_available} gün
                          </div>
                          {/* Devir ve bu yıl detayı */}
                          <div className="flex gap-1 text-xs">
                            {person.remaining_carryover > 0 && (
                              <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded" title={`${person.carryover_from_year}'den devir`}>
                                📦 {person.remaining_carryover}
                              </span>
                            )}
                            <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded" title="Bu yılın hakkı">
                              📅 {person.remaining_leave}/{person.annual_leave_days || 20}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className={`text-center ${getLeaveColumnClass('social')}`}>
                        <span className="text-sm font-medium text-purple-600">{person.social_remaining}/12</span>
                      </TableCell>
                      <TableCell className={`text-center ${getLeaveColumnClass('report')}`}>
                        <span className="text-sm font-medium text-pink-600">{person.report_remaining}/40</span>
                      </TableCell>
                      <TableCell className={`text-center ${getLeaveColumnClass('other')}`}>
                        <span className="text-sm font-medium text-gray-600">{person.other_count || 0}</span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>

          {/* Pagination */}
          {totalStatusPages > 1 && (
            <div className="flex justify-center items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStatusCurrentPage(p => Math.max(1, p - 1))}
                disabled={statusCurrentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-gray-600">
                Sayfa {statusCurrentPage} / {totalStatusPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStatusCurrentPage(p => Math.min(totalStatusPages, p + 1))}
                disabled={statusCurrentPage === totalStatusPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      {activeSubTab === 'return_history' && (
        <div className="space-y-4">
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                <div className="md:col-span-2 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Personel / not / izin türü ara..."
                    value={returnHistorySearch}
                    onChange={(e) => setReturnHistorySearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchLeaveReturnHistory()}
                    className="pl-10"
                  />
                </div>
                <SearchableSelect
                  value={returnHistoryEarlyFilter}
                  onChange={setReturnHistoryEarlyFilter}
                  allLabel="Erken Dönüş (Tümü)"
                  allValue="all"
                  searchPlaceholder="Erken dönüş ara..."
                  options={[
                    { id: "true", name: "Sadece Erken Dönüş" },
                    { id: "false", name: "Sadece Normal Dönüş" },
                  ]}
                />
                <SearchableSelect
                  value={returnHistoryModeFilter}
                  onChange={setReturnHistoryModeFilter}
                  allLabel="Tüm Planlar"
                  allValue="all"
                  searchPlaceholder="Vekalet planı ara..."
                  options={[
                    { id: "immediate", name: "Hemen Bitir" },
                    { id: "end_of_day", name: "Gün Sonu" },
                    { id: "custom", name: "Özel Tarih" },
                  ]}
                />
                <div className="md:col-span-2">
                  <DateRangePicker
                    startDate={returnHistoryFromDate}
                    endDate={returnHistoryToDate}
                    onChange={({ start, end }) => {
                      setReturnHistoryFromDate(start);
                      setReturnHistoryToDate(end);
                    }}
                    placeholder="Dönüş tarih aralığı"
                    highlightHolidays
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <Button variant="outline" onClick={() => {
                  setReturnHistorySearch('');
                  setReturnHistoryEarlyFilter('all');
                  setReturnHistoryModeFilter('all');
                  setReturnHistoryFromDate('');
                  setReturnHistoryToDate('');
                }}>
                  Temizle
                </Button>
                <Button onClick={fetchLeaveReturnHistory} className="bg-orange-500 hover:bg-orange-600 text-white">
                  Ara
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              {returnHistoryLoading ? (
                <div className="p-10 text-center text-gray-500">İşbaşı geçmişi yükleniyor...</div>
              ) : returnHistory.length === 0 ? (
                <div className="p-10 text-center text-gray-500">Kayıt bulunamadı.</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Personel</TableHead>
                        <TableHead>İzin Aralığı</TableHead>
                        <TableHead>Planlanan / Fiili</TableHead>
                        <TableHead>Erken Dönüş</TableHead>
                        <TableHead>Vekalet Planı</TableHead>
                        <TableHead>İade Gün</TableHead>
                        <TableHead>Not</TableHead>
                        <TableHead>Kaydı Giren</TableHead>
                        {(isAdmin || isMudur) && <TableHead>İşlem</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {returnHistory.map((row) => (
                        <TableRow key={row.leave_request_id}>
                          <TableCell>
                            <div className="font-medium">{row.personnel_name}</div>
                            <div className="text-xs text-gray-500">{row.reason_type || '-'}</div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {row.start_date ? new Date(row.start_date).toLocaleDateString('tr-TR') : '-'}
                              {' - '}
                              {row.end_date ? new Date(row.end_date).toLocaleDateString('tr-TR') : '-'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-xs text-gray-600">
                              Plan: {row.return_date ? new Date(row.return_date).toLocaleDateString('tr-TR') : '-'}
                            </div>
                            <div className="text-sm font-semibold text-gray-800">
                              Fiili: {row.actual_return_date ? new Date(row.actual_return_date).toLocaleDateString('tr-TR') : '-'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={row.returned_early ? 'bg-red-500' : 'bg-green-600'}>
                              {row.returned_early ? 'Erken' : 'Normal'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {row.handover_mode === 'immediate' ? 'Hemen bitir'
                                : row.handover_mode === 'end_of_day' ? 'Gün sonu'
                                : row.handover_mode === 'custom' ? 'Özel tarih'
                                : '-'}
                            </div>
                            {row.handover_until && (
                              <div className="text-xs text-gray-500">
                                Bitiş: {new Date(row.handover_until).toLocaleDateString('tr-TR')}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-blue-50 border-blue-300 text-blue-700">
                              {row.leave_days_refunded || 0}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-[260px] truncate" title={row.return_note || ''}>
                            {row.return_note || '-'}
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">{row.return_marked_by_name || row.personnel_name || '-'}</div>
                            <div className="text-xs text-gray-500">
                              {row.return_marked_at ? new Date(row.return_marked_at).toLocaleString('tr-TR') : '-'}
                            </div>
                          </TableCell>
                          {(isAdmin || isMudur) && (
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button size="sm" variant="outline" onClick={() => handleReturnHistoryAction(row, 'revert')} title="İade Et">
                                  ↩️
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleReturnHistoryAction(row, 'cancel')} title="İptal Et">
                                  🚫
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => handleReturnHistoryAction(row, 'delete')} title="Sil">
                                  🗑️
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LeaveManagementPanel;
